import { BASE_URL } from '../../constants';
import { secureFetch } from '../../utils/secureFetch';
import { announceSessionEnded, parseHttpError, sessionEndedMessage } from './userActions';

// Resolves with the athlete doc, or null when it could not be loaded.
export const fetchAthleteProfileByEmail = (email) => async (dispatch, getState) => {
  const token = getState().auth?.token;
  // A log out or another sign-in happened while this was in flight: the doc
  // belongs to the old session and must not land in the new one.
  const stale = () => !!token && getState().auth?.token !== token;
  try {
    const data = await secureFetch({ method: 'GET', url: `${BASE_URL}/api/users/athlete/by-email/?email=${encodeURIComponent(email)}`, getState });
    if (stale()) return null;
    dispatch({ type: 'GET_ATHLETE_PROFILE', payload: data });
    return data;
  } catch (err) {
    console.error('fetchAthleteProfileByEmail error:', err);
    const { status, body } = parseHttpError(err);
    if (token && !stale() && status === 401) announceSessionEnded(sessionEndedMessage(status, body));
    return null;
  }
};

export const fetchAthleteProfile = () => async (dispatch, getState) => {
  try {
    const data = await secureFetch({ method: 'GET', url: `${BASE_URL}/api/users/athlete/me/`, getState });
    dispatch({ type: 'GET_ATHLETE_PROFILE', payload: data });
  } catch (err) {
    console.error('fetchAthleteProfile error:', err);
  }
};

// Rethrows, as saveAthleteProfileAsCircleMember does, so the editor can show
// the server's refusal (a rename away from the athlete's roster spot, a
// birthday it will not take, a locked account) instead of reloading the old
// values without a word. profileSaveErrorMessage turns the error into text.
export const saveAthleteProfile = (profile) => async (dispatch, getState) => {
  try {
    const data = await secureFetch({ method: 'PUT', url: `${BASE_URL}/api/users/athlete/update/`, body: profile, getState });
    dispatch({ type: 'UPDATE_PROFILE', payload: data });
    return data;
  } catch (err) {
    console.error('saveAthleteProfile error:', err);
    throw err;
  }
};

export const PROFILE_SAVE_FAILED_MESSAGE = 'Could not save the profile.';

// The words to show for a refused profile save: the server's `message`, else
// its `error` or `detail` when that is a sentence (never a bare code such as
// name_mismatch), else the first field message, else `fallback`. A body that
// is not JSON (an HTML error page) also gets `fallback`.
export const profileSaveErrorMessage = (err, fallback = PROFILE_SAVE_FAILED_MESSAGE) => {
  const { body } = parseHttpError(err);
  if (!body || typeof body !== 'object') return fallback;
  const text = (v) => (typeof v === 'string' ? v.trim() : '');
  const isCode = (v) => /^[a-z0-9_.-]+$/.test(v);
  const message = text(body.message);
  if (message) return message;
  const sentence = [text(body.error), text(body.detail)].find((v) => v && !isCode(v));
  if (sentence) return sentence;
  const fields = body.fields && typeof body.fields === 'object' ? Object.values(body.fields) : [];
  const field = fields.map((f) => text(Array.isArray(f) ? f[0] : f)).find(Boolean);
  return field || fallback;
};

export const clearAthleteProfile = () => ({
  type: 'CLEAR_ATHLETE_PROFILE',
});

export const fetchAllInterests = () => async (dispatch, getState) => {
  try {
    const data = await secureFetch({ method: 'GET', url: `${BASE_URL}/api/users/athlete/interests/all/`, getState });
    dispatch({ type: 'FETCH_ALL_INTERESTS', payload: data['interests'] });
  } catch (err) {
    console.error('fetchAllInterests error:', err);
  }
}
